import { useEffect, useMemo, useRef, useState } from "react"

import {
  Badge,
  Button,
  Card,
  cn,
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTitle,
  Loader,
  SearchInput,
  toast,
} from "@coval/ui"
import { ArrowRight, Plus, X } from "lucide-react"

import { PickerCatalogContent } from "@/components/entity-picker/PickerCatalogContent"
import type { PersonaCatalogItemResource } from "@/types_api/backend/transport/list_persona_catalog_entries_response"

import { usePersonaCatalogEntriesTransport } from "../routes/usePersonaCatalogTransport"
import { isGenericPersonaCatalogCategory } from "../utils/isGenericPersonaCatalogCategory"
import { AddPersonaCard } from "./AddPersonaCard"

interface AddPersonaModalProps {
  isOpen: boolean
  onClose: () => void
  onCreate?: () => void
  addLabel?: string
  doneLabel?: string
  presentation?: "dialog" | "picker-panel"
  onAdd: (entryId: string) => Promise<void> | void
}

export function AddPersonaModal({
  isOpen,
  onClose,
  onCreate,
  onAdd,
  addLabel,
  doneLabel = "Done",
  presentation = "dialog",
}: AddPersonaModalProps) {
  const isPickerPanel = presentation === "picker-panel"
  const [search, setSearch] = useState("")
  const [sessionAddCount, setSessionAddCount] = useState(0)
  const [sessionAddedEntryIds, setSessionAddedEntryIds] = useState<Set<string>>(
    () => new Set(),
  )
  const sessionGenerationRef = useRef(0)
  const { data, error, isLoading, refetch } =
    usePersonaCatalogEntriesTransport()
  const hasData = data !== undefined

  useEffect(() => {
    if (isOpen) {
      void refetch()
    }
  }, [isOpen, refetch])

  const { drafts, groups } = useMemo(() => {
    const query = search.trim().toLowerCase()
    const entries = (data?.entries ?? []).filter((entry) =>
      [
        entry.display_name,
        entry.description,
        entry.category ?? "",
        entry.persona_display_name,
        ...entry.tags,
      ].some((value) => value.toLowerCase().includes(query)),
    )
    const drafts = entries
      .filter((entry) => entry.status === "inactive")
      .sort((left, right) =>
        left.display_name.localeCompare(right.display_name),
      )
    const grouped = new Map<string, PersonaCatalogItemResource[]>()

    for (const entry of entries.filter(
      (candidate) => candidate.status !== "inactive",
    )) {
      const category = entry.category?.trim() || "Other"
      const existing = grouped.get(category)
      if (existing) {
        existing.push(entry)
      } else {
        grouped.set(category, [entry])
      }
    }

    const groups = Array.from(grouped, ([category, items]) => ({
      category,
      items: items.sort((left, right) =>
        left.display_name.localeCompare(right.display_name),
      ),
    })).sort((left, right) => left.category.localeCompare(right.category))

    return { drafts, groups }
  }, [data, search])

  useEffect(
    () => () => {
      sessionGenerationRef.current += 1
    },
    [],
  )

  const handleAdd = async (entryId: string) => {
    const sessionGeneration = sessionGenerationRef.current
    try {
      await onAdd(entryId)
    } catch (error: unknown) {
      if (sessionGeneration !== sessionGenerationRef.current) {
        return
      }
      toast.error({
        title: "Error adding persona",
        error,
        fallback: "Unable to add persona.",
      })
      return
    }
    if (sessionGeneration !== sessionGenerationRef.current) {
      return
    }
    setSessionAddedEntryIds((entryIds) => new Set(entryIds).add(entryId))
    setSessionAddCount((count) => count + 1)
  }

  const resetSession = () => {
    sessionGenerationRef.current += 1
    setSearch("")
    setSessionAddCount(0)
    setSessionAddedEntryIds(new Set())
  }

  const handleClose = () => {
    resetSession()
    onClose()
  }

  const handleCreate = () => {
    resetSession()
    onCreate?.()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <PickerCatalogContent
        presentation={presentation}
        className="h-[84vh] w-full max-w-4xl"
      >
        <DialogHeader className="border-b border-coval-border pb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <DialogTitle className="text-base">
              {isPickerPanel ? "Persona Catalog" : "Add a persona"}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <SearchInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onClear={() => setSearch("")}
                placeholder="Search personas…"
                aria-label="Search persona catalog"
                className="w-48 sm:w-64"
              />
              <Button
                variant="secondary"
                icon
                onClick={handleClose}
                tooltip="Close"
              >
                <X />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="min-h-0 overflow-y-auto p-6">
          {onCreate && (
            <div className="rounded-md border border-coval-border bg-surface-1 p-3.5">
              <div className="mb-2.5 flex items-baseline gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-coval-text">
                  Create
                </h3>
                <span className="text-xs text-coval-text-muted">
                  Define a new persona from scratch
                </span>
              </div>
              <Card
                interactive
                className="flex w-full items-start gap-2 border-coval-border-active p-3 text-left"
                onClick={handleCreate}
              >
                <Plus className="mt-0.5 size-4 shrink-0 text-coval-text-muted" />
                <span className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-coval-text">
                    Create Your Own
                  </span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-coval-text-muted">
                    Start with a blank persona and make it yours.
                  </span>
                </span>
                <ArrowRight className="mt-0.5 size-3.5 shrink-0 text-coval-text-muted" />
              </Card>
            </div>
          )}
          <div className="my-5 flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-coval-text-muted">
              Catalog
            </span>
            <hr className="flex-1 border-t border-coval-border" />
          </div>

          {error && (
            <div
              className="mb-4 flex items-center justify-between gap-3 rounded-md border border-coval-border bg-surface-default px-3 py-2"
              role="alert"
            >
              <span className="text-sm text-coval-text-muted">
                {hasData
                  ? "Couldn't refresh the persona catalog."
                  : "Couldn't load the persona catalog."}
              </span>
              <Button
                variant="secondary"
                className="h-7 px-2.5 text-xs"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            </div>
          )}

          {isLoading && !hasData && !error && (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          )}

          {hasData && drafts.length === 0 && groups.length === 0 && (
            <div className="py-8 text-center text-sm text-coval-text-muted">
              {search.trim()
                ? `No catalog personas match “${search.trim()}”`
                : "No catalog personas yet."}
            </div>
          )}

          <div className={cn("space-y-6", !hasData && "hidden")}>
            {drafts.length > 0 && (
              <section
                aria-labelledby="draft-persona-catalog-heading"
                className="rounded-md border border-coval-border bg-surface-1 p-3"
              >
                <div className="mb-2.5 flex items-start justify-between gap-3">
                  <div>
                    <h3
                      className="text-sm font-semibold text-coval-text"
                      id="draft-persona-catalog-heading"
                    >
                      Drafts
                    </h3>
                    <p className="mt-0.5 text-xs text-coval-text-muted">
                      Inactive catalog entries available for staging testing.
                    </p>
                  </div>
                  <Badge variant="outline">{drafts.length}</Badge>
                </div>
                <div
                  className={cn(
                    "grid grid-cols-1 gap-2 lg:grid-cols-2",
                    isPickerPanel && "md:grid-cols-2",
                  )}
                >
                  {drafts.map((item) => (
                    <AddPersonaCard
                      key={item.entry_id}
                      isAdded={sessionAddedEntryIds.has(item.entry_id)}
                      item={item}
                      onAdd={handleAdd}
                      addLabel={addLabel}
                    />
                  ))}
                </div>
              </section>
            )}

            {groups.map((group) => (
              <section key={group.category}>
                {!isGenericPersonaCatalogCategory(group.category) && (
                  <div className="mb-2.5 flex items-baseline gap-2">
                    <h3 className="text-sm font-semibold text-coval-text">
                      {group.category}
                    </h3>
                  </div>
                )}
                <div
                  className={cn(
                    "grid grid-cols-1 gap-2 lg:grid-cols-2",
                    isPickerPanel && "md:grid-cols-2",
                  )}
                >
                  {group.items.map((item) => (
                    <AddPersonaCard
                      key={item.entry_id}
                      isAdded={sessionAddedEntryIds.has(item.entry_id)}
                      item={item}
                      onAdd={handleAdd}
                      addLabel={addLabel}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </DialogBody>

        <div className="flex items-center gap-3 border-t border-coval-border px-6 py-3">
          <span className="text-xs text-coval-text-muted">
            {sessionAddCount === 0
              ? "Nothing added yet"
              : `${sessionAddCount} persona${sessionAddCount > 1 ? "s" : ""} added`}
          </span>
          <div className="flex-1" />
          <Button onClick={handleClose}>{doneLabel}</Button>
        </div>
      </PickerCatalogContent>
    </Dialog>
  )
}
